import { BlogContentPrompt } from "@/hooks/blog/useBlogAI";
import { Stakeholder } from "@freetech/models/user";
import { ProjectIdeaForm, ProjectPlan } from "@freetech/models/projects";
import { axiosClient } from "../axiosClient";

export const ai = {
  shared: {
    sendMessage: async (
      prompt: string,
      context?: {
        boardId?: string;
        taskId?: string;
        clientId?: string;
        chatThreadId?: string;
      }
    ) => {
      return await axiosClient.post("/ai/shared/ai-message", {
        prompt,
        context,
      });
    },
  },

  admin: {
    generateBlogContent: async (prompt: BlogContentPrompt): Promise<any> => {
      return await axiosClient.post("/ai/admin/generate-blog-content", {
        prompt,
      });
    },

    generateEmailTemplate: async (prompt: {
      prompt: string;
      templateType?:
        | "general"
        | "portal_notification"
        | "misc"
        | "marketing_campaign"
        | "newsletter";
      requiredVariables?: string[];
      tone?: string;
      purpose?: string;
      brandName?: string;
      includeHeader?: boolean;
      includeFooter?: boolean;
      colorScheme?: string;
    }): Promise<any> => {
      return await axiosClient.post("/ai/admin/generate-email-template", {
        prompt,
      });
    },

    enhanceEmailTemplate: async (prompt: {
      prompt: string;
      existingTemplate: {
        name: string;
        subject: string;
        html: string;
        description?: string;
        variables: string[];
      };
      templateType?:
        | "general"
        | "portal_notification"
        | "misc"
        | "marketing_campaign"
        | "newsletter";
      tone?: string;
      purpose?: string;
      brandName?: string;
      includeHeader?: boolean;
      includeFooter?: boolean;
      colorScheme?: string;
      requiredVariables?: string[];
    }): Promise<any> => {
      return await axiosClient.post("/ai/admin/enhance-email-template", {
        prompt,
      });
    },
  },

  stakeholder: {
    suggestProjectType: async (description: string) => {
      return await axiosClient.post("/ai/stakeholder/project-idea/suggest-project-type", {
        description,
      });
    },
    suggestRelevantClient: async (projectDescription: string) => {
      return await axiosClient.post("/ai/stakeholder/project-idea/suggest-relevant-client", {
        projectDescription,
      });
    },
    generateProjectPlan: async ({
      clientId,
      projectIdeaId,
    }: {
      clientId: string;
      projectIdeaId: string;
    }): Promise<{ success: boolean; plan: ProjectPlan }> => {
      return await axiosClient.post("/ai/stakeholder/project-idea/generate-project-plan", {
        clientId,
        projectIdeaId,
      });
    },
    handleConversation: async (
      messages: { role: string; content: string }[],
      currentStep: string,
      clientId: string,
      projectIdeaId?: string
    ): Promise<any> => {
      return await axiosClient.post("/ai/stakeholder/project-idea/handle-conversation", {
        messages,
        currentStep,
        clientId,
        projectIdeaId,
      });
    },
    submitStakeholderProjectIdea: async ({
      clientId,
      projectIdeaId,
      projectPlanId,
    }: {
      clientId: string;
      projectIdeaId: string;
      projectPlanId: string;
    }): Promise<{ success: boolean; projectId: string }> => {
      return await axiosClient.post("/ai/stakeholder/project-idea/submit-project-idea", {
        clientId,
        projectIdeaId,
        projectPlanId,
      });
    },
    initializeProjectIdea: async (data: {
      clientId: string;
      projectName: string;
      projectType: string;
      projectDescription: string;
      budget?: number;
      timeline?: string;
      expectedStartDate?: string;
    }): Promise<{
      success: boolean;
      projectIdeaId: string;
      projectData: any;
    }> => {
      return await axiosClient.post("/ai/stakeholder/project-idea/initialize-project-idea", data);
    },
  },
};
